import {
  RequestsQuery,
  RequestsStoreOrdersQuery,
  SortByInput,
  SortOrder,
  useAutocompleteLocationsQuery,
  useAutocompleteShippingLocationGroupsQuery,
  useRequestCountsQuery,
  useRequestsDefaultsQuery,
  useRequestsFiltersQuery,
  useRequestsQuery,
  useRequestsStoreOrdersQuery,
  useRfqCreatedSubscription,
  useRfqUserAssignedSubscription,
  useShippingLocationGroupsQuery,
} from '@/buyers/_gen/gql'
import Frame from '@/buyers/components/Frame'
import RfqStepBadge from '@/buyers/components/RfqStepBadge'
import useApolloWsClient from '@/buyers/hooks/useApolloWsClient'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Dropdown from '@/gf/components/Dropdown'
import FilterAction from '@/gf/components/FilterAction'
import FilterDropdown from '@/gf/components/FilterDropdown'
import FilterProgressNav from '@/gf/components/FilterProgressNav'
import Link from '@/gf/components/Link'
import MachineDownIcon from '@/gf/components/MachineDownIcon'
import Page from '@/gf/components/Page'
import PaginationC from '@/gf/components/Pagination'
import SortByHeaderButton from '@/gf/components/SortByHeaderButton'
import Spinner from '@/gf/components/Spinner'
import { TableV2 as Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import TimeOpenPill from '@/gf/components/TimeOpenPill'
import DelayedSearchInput from '@/gf/components/inputs/DelayedSearch'
import usePage from '@/gf/hooks/usePage'
import useWindowWidth from '@/gf/hooks/useWindowWidth'
import RequestForQuoteM from '@/gf/modules/RequestForQuote'
import {
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  InboxIcon,
  PaperAirplaneIcon,
  ArrowUturnLeftIcon,
  TruckIcon,
} from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { DateTime } from 'luxon'
import { useEffect, useMemo, useRef, useState } from 'react'
import { BooleanParam, JsonParam, StringParam, useQueryParams, withDefault } from 'use-query-params'
import AssignUserDropdown from '../components/AssignUserDropdown'
import ActionCells from './Requests/ActionCells'
import Actions from './Requests/Actions'
import PartsCell from './Requests/PartsCell'
import Stores from './Requests/Stores'
import Vendors from './Requests/Vendors'
import { filterDefaultUpdates } from './Requests/filterDefaults'
import useDebounce from '@/gf/hooks/useDebounce'
import { Popover, PopoverContent, PopoverTrigger } from '@/gf/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
  CommandSeparator,
} from '@/gf/components/ui/command'
import RedAlert from '@/gf/components/RedAlert'
import Action from '@/gf/components/Action'
import { ChevronsUpDown } from 'lucide-react'
import Checkbox from '@/gf/components/next/forms/Checkbox'

export type Request = RequestsQuery['searchRequests']['requests'][number]

// Special value to represent "No location group" filter option
const NO_LOCATION_GROUP = '__no_location_group__'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Requests', href: '/rfqs' }],
}

const allSteps = [
  { id: 'all', name: 'All', icon: InboxIcon },
  { id: 'inbound', name: 'Inbound', icon: InboxIcon },
  { id: 'submitted', name: 'Submitted', icon: PaperAirplaneIcon },
  { id: 'quoted', name: 'Quoted', icon: ArrowUturnLeftIcon },
  { id: 'po_sent', name: 'PO Sent', icon: ClipboardDocumentCheckIcon },
  { id: 'fulfilling', name: 'Fulfilling', icon: TruckIcon },
  { id: 'fulfilled', name: 'Fulfilled', icon: CheckCircleIcon },
]

const now = DateTime.now()

const useQueryParamsOpts = {
  selected: StringParam,
  search: StringParam,
  sortBy: withDefault<SortByInput, SortByInput>(JsonParam, {
    field: 'inserted_at',
    order: SortOrder.Desc,
  }),
  highPriority: withDefault(BooleanParam, false),
  backordered: withDefault(BooleanParam, false),
  myRequests: withDefault(BooleanParam, false),
  unassigned: withDefault(BooleanParam, false),
  'has-problems': withDefault(BooleanParam, false),
  'delivery-method': withDefault(StringParam, null),
  locationIds: withDefault<string[], string[]>(JsonParam, []),
  locationGroupIds: withDefault<string[], string[]>(JsonParam, []),
}

const LocationCheckboxCombobox = ({
  locationIds,
  onChange,
}: {
  locationIds: string[]
  onChange: (locationIds: string[]) => void
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { organization } = useSession()

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useAutocompleteLocationsQuery({
    client,
    variables: {
      orgId: organization.id,
      filter: JSON.stringify(['and', ['is_not_deleted'], ['search', search]]),
    },
  })

  const { data: selectedLocationsData, error: selectedLocationsError } =
    useAutocompleteLocationsQuery({
      client,
      variables: {
        orgId: organization.id,
        filter: JSON.stringify(['id_in', locationIds]),
      },
    })

  const [selectedLocations, setSelectedLocations] = useState(
    selectedLocationsData?.org?.locations ?? []
  )
  const selectedLocationIds = useMemo(() => selectedLocations.map((s) => s.id), [selectedLocations])

  const searchLocations = useMemo(() => {
    return searchData?.org?.locations.filter((l) => !selectedLocationIds.includes(l.id)) ?? []
  }, [searchData, selectedLocationIds])

  // Only update the selected group after the popover is closed
  useEffect(() => {
    if (!open) {
      setSelectedLocations(selectedLocationsData?.org?.locations ?? [])
    }
  }, [open, selectedLocationsData])

  const ActionComponent = locationIds.length > 0 ? Action.P : Action.S

  const LocationCheckboxItem = ({ location }: { location: { id: string; name: string } }) => {
    const selected = locationIds.some((s) => s === location.id)
    const onSelectionChange = () =>
      onChange(
        selected ? locationIds.filter((s) => s !== location.id) : [...locationIds, location.id]
      )
    return (
      <CommandItem
        key={location.id}
        className="group"
        value={location.id}
        onSelect={() => {
          setSearch('')
          setOpen(false)
          onSelectionChange()
        }}
      >
        <Checkbox
          className={!selected ? 'group-hover:visible invisible' : undefined}
          checked={selected}
          color="blue"
          onChange={() => onSelectionChange()}
          onClick={(e) => e.stopPropagation()}
        />
        {location.name}
      </CommandItem>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div>
          <ActionComponent className="flex items-center gap-x-1" size="sm">
            <span className="truncate max-w-32">
              {locationIds.length === 0
                ? 'Location'
                : locationIds.length === 1
                  ? (selectedLocationsData?.org?.locations.find((l) => l.id === locationIds[0])
                      ?.name ?? 'Loading...')
                  : `${locationIds.length} Locations`}
            </span>
            <ChevronsUpDown className="-mr-1 w-4 h-4 inline-block" />
          </ActionComponent>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Select location..." value={search} onValueChange={setSearch} />
          <CommandList>
            {searchError || selectedLocationsError ? (
              <RedAlert className="bg-transparent" title="Error searching locations." />
            ) : searchLoading ? (
              <CommandLoading>
                <div className="flex flex-row gap-x-2 items-center justify-center">
                  <Spinner className="mx-0" size="xs" color="blue" />
                  Searching units of measure...
                </div>
              </CommandLoading>
            ) : !debouncedSearch ? (
              <CommandEmpty>Search for unit of measure.</CommandEmpty>
            ) : (
              <CommandEmpty>No units found.</CommandEmpty>
            )}
            {selectedLocations.length > 0 && (
              <>
                <CommandGroup>
                  {selectedLocations.map((location) => (
                    <LocationCheckboxItem key={location.id} location={location} />
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {searchLocations.map((location) => (
                <LocationCheckboxItem key={location.id} location={location} />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

const LocationGroupCheckboxCombobox = ({
  locationGroupIds,
  onChange,
}: {
  locationGroupIds: string[]
  onChange: (locationGroupIds: string[]) => void
}) => {
  const client = useGqlClient()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { organization } = useSession()

  // Filter out the special NO_LOCATION_GROUP value when querying for real location groups
  const realLocationGroupIds = useMemo(
    () => locationGroupIds.filter((id) => id !== NO_LOCATION_GROUP),
    [locationGroupIds]
  )
  const hasNoLocationGroupSelected = locationGroupIds.includes(NO_LOCATION_GROUP)

  const {
    data: searchData,
    loading: searchLoading,
    error: searchError,
  } = useAutocompleteShippingLocationGroupsQuery({
    client,
    variables: {
      orgId: organization.id,
      filter: JSON.stringify(['search', search]),
    },
  })

  const { data: selectedLocationGroupsData, error: selectedLocationGroupsError } =
    useShippingLocationGroupsQuery({
      client,
      variables: {
        orgId: organization.id,
        filter: JSON.stringify(['id_in', realLocationGroupIds]),
      },
      skip: realLocationGroupIds.length === 0,
    })

  const [selectedLocationGroups, setSelectedLocationGroups] = useState(
    selectedLocationGroupsData?.org?.shippingLocationGroups ?? []
  )
  const selectedLocationGroupIds = useMemo(
    () => selectedLocationGroups.map((s) => s.id),
    [selectedLocationGroups]
  )

  const searchLocationGroups = useMemo(() => {
    return (
      searchData?.org?.shippingLocationGroups.filter(
        (l) => !selectedLocationGroupIds.includes(l.id)
      ) ?? []
    )
  }, [searchData, selectedLocationGroupIds])

  // Only update the selected group after the popover is closed
  useEffect(() => {
    if (!open) {
      setSelectedLocationGroups(selectedLocationGroupsData?.org?.shippingLocationGroups ?? [])
    }
  }, [open, selectedLocationGroupsData])

  const ActionComponent = locationGroupIds.length > 0 ? Action.P : Action.S

  const LocationGroupCheckboxItem = ({
    locationGroup,
  }: {
    locationGroup: { id: string; name: string }
  }) => {
    const selected = locationGroupIds.some((s) => s === locationGroup.id)
    const onSelectionChange = () =>
      onChange(
        selected
          ? locationGroupIds.filter((s) => s !== locationGroup.id)
          : [...locationGroupIds, locationGroup.id]
      )
    return (
      <CommandItem
        key={locationGroup.id}
        className="group"
        value={locationGroup.id}
        onSelect={() => {
          setSearch('')
          setOpen(false)
          onSelectionChange()
        }}
      >
        <Checkbox
          className={!selected ? 'group-hover:visible invisible' : undefined}
          checked={selected}
          color="blue"
          onChange={() => onSelectionChange()}
          onClick={(e) => e.stopPropagation()}
        />
        {locationGroup.name}
      </CommandItem>
    )
  }

  // Get the display text for the button
  const getButtonText = () => {
    if (locationGroupIds.length === 0) return 'Location Group'
    if (locationGroupIds.length === 1) {
      if (hasNoLocationGroupSelected) return 'No location group'
      return (
        selectedLocationGroupsData?.org?.shippingLocationGroups.find(
          (l) => l.id === locationGroupIds[0]
        )?.name ?? 'Loading...'
      )
    }
    return `${locationGroupIds.length} Location Groups`
  }

  const toggleNoLocationGroup = () => {
    onChange(
      hasNoLocationGroupSelected
        ? locationGroupIds.filter((s) => s !== NO_LOCATION_GROUP)
        : [...locationGroupIds, NO_LOCATION_GROUP]
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div>
          <ActionComponent className="flex items-center gap-x-1" size="sm">
            <span className="truncate max-w-32">{getButtonText()}</span>
            <ChevronsUpDown className="-mr-1 w-4 h-4 inline-block" />
          </ActionComponent>
        </div>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Select location group..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {searchError || selectedLocationGroupsError ? (
              <RedAlert className="bg-transparent" title="Error searching locations." />
            ) : searchLoading ? (
              <CommandLoading>
                <div className="flex flex-row gap-x-2 items-center justify-center">
                  <Spinner className="mx-0" size="xs" color="blue" />
                  Searching location groups...
                </div>
              </CommandLoading>
            ) : !debouncedSearch ? (
              <CommandEmpty>Search for location group.</CommandEmpty>
            ) : (
              <CommandEmpty>No location groups found.</CommandEmpty>
            )}
            {/* "No location group" option - always show at top when selected or when no search */}
            {(hasNoLocationGroupSelected || !debouncedSearch) && (
              <>
                <CommandGroup>
                  <CommandItem
                    className="group"
                    value={NO_LOCATION_GROUP}
                    onSelect={() => {
                      setSearch('')
                      setOpen(false)
                      toggleNoLocationGroup()
                    }}
                  >
                    <Checkbox
                      className={
                        !hasNoLocationGroupSelected ? 'group-hover:visible invisible' : undefined
                      }
                      checked={hasNoLocationGroupSelected}
                      color="blue"
                      onChange={toggleNoLocationGroup}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span className="italic">No location group</span>
                  </CommandItem>
                </CommandGroup>
                {(selectedLocationGroups.length > 0 || searchLocationGroups.length > 0) && (
                  <CommandSeparator />
                )}
              </>
            )}
            {selectedLocationGroups.length > 0 && (
              <>
                <CommandGroup>
                  {selectedLocationGroups.map((locationGroup) => (
                    <LocationGroupCheckboxItem
                      key={locationGroup.id}
                      locationGroup={locationGroup}
                    />
                  ))}
                </CommandGroup>
                {searchLocationGroups.length > 0 && <CommandSeparator />}
              </>
            )}
            <CommandGroup>
              {searchLocationGroups.map((locationGroup) => (
                <LocationGroupCheckboxItem key={locationGroup.id} locationGroup={locationGroup} />
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

// Session storage keys for caching user's default filters
const getLocationGroupsCacheKey = (userId: string, orgId: string) =>
  `gf_user_${userId}_org_${orgId}_location_groups`

const Requests = () => {
  const {
    orgId,
    user: { id: userId },
    organization,
  } = useSession()

  const client = useGqlClient()
  const [page, setPage] = usePage()
  const [showActionDivider, setShowActionDivider] = useState<boolean>()
  const tableRef = useRef<HTMLDivElement>(null)
  const windowWidth = useWindowWidth(300)
  // Read the cached location groups synchronously so the very first counts and
  // requests queries already carry the filter. Previously this was applied in a
  // useEffect after the first render, which fired the queries once without the
  // filter and then again with it — a double load that was painfully slow for
  // large orgs (GEA-2185). Falls back to [] when there is no cache.
  const defaultLocationGroupIds = useMemo<string[]>(() => {
    try {
      const cached = sessionStorage.getItem(getLocationGroupsCacheKey(userId, orgId))
      if (cached) {
        const cachedIds = JSON.parse(cached)
        if (Array.isArray(cachedIds) && cachedIds.length > 0) return cachedIds
      }
    } catch {
      // Ignore session storage errors
    }
    return []
  }, [])

  const queryParamsOpts = useMemo(
    () => ({
      ...useQueryParamsOpts,
      locationGroupIds: withDefault<string[], string[]>(JsonParam, defaultLocationGroupIds),
    }),
    [defaultLocationGroupIds]
  )

  const [query, updateQuery] = useQueryParams(queryParamsOpts)

  // Track whether IDs came from the URL (authoritative — not overwritten by the
  // cache/notification defaults). Read the raw param so a cache-derived default
  // is not mistaken for a URL-provided value.
  const locationGroupIdsFromUrl = useRef(
    new URLSearchParams(window.location.search).has('locationGroupIds')
  )

  // The user's default filters. Requests and RequestCounts wait for them (see the effect
  // that sets filtersReady), so each runs once per page load with the right filters.
  const { data: defaultsData, error: defaultsError } = useRequestsDefaultsQuery({
    client,
    variables: { userId },
    fetchPolicy: 'no-cache',
  })

  const [filtersReady, setFiltersReady] = useState(false)

  const apiFilters = (stepId: string) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filters: any[] = []
    if (stepId !== 'all') filters.push(['step_eq', stepId])
    if (query.search && query.search.length > 0) filters.push(['search', query.search])
    if (query.highPriority) filters.push(['is_high_priority'])
    if (query.backordered) filters.push(['is_backordered'])
    if (query.myRequests) filters.push(['is_assigned_to', userId])
    if (query.unassigned) filters.push(['is_unassigned'])
    if (query['has-problems']) filters.push(['has_problems'])
    if (query['delivery-method']) filters.push(['delivery_method_eq', query['delivery-method']])
    if (query.locationIds && query.locationIds.length > 0)
      filters.push(['shipping_location_id_in', query.locationIds])

    // Handle location group filter with special "No location group" option
    if (query.locationGroupIds && query.locationGroupIds.length > 0) {
      const hasNoLocationGroup = query.locationGroupIds.includes(NO_LOCATION_GROUP)
      const realGroupIds = query.locationGroupIds.filter((id) => id !== NO_LOCATION_GROUP)

      if (hasNoLocationGroup && realGroupIds.length > 0) {
        // Both "No location group" and specific groups selected - use OR
        filters.push([
          'or',
          ['has_no_shipping_location_group'],
          ['shipping_location_group_id_in', realGroupIds],
        ])
      } else if (hasNoLocationGroup) {
        // Only "No location group" selected
        filters.push(['has_no_shipping_location_group'])
      } else {
        // Only specific groups selected
        filters.push(['shipping_location_group_id_in', realGroupIds])
      }
    }

    const filter = filters.length > 1 ? ['and', ...filters] : filters.length === 1 ? filters[0] : []
    return JSON.stringify(filter)
  }

  // All step tab counts come back from a single query (one aggregation pass)
  // instead of six separate rfqCount queries. The base filter carries the
  // non-step filters (search, location groups); the per-step counts are
  // computed server-side. See GEA-2185.
  const { data: countsData, refetch: refetchCounts } = useRequestCountsQuery({
    client,
    variables: {
      orgId,
      baseFilter: apiFilters('all'),
    },
    skip: !filtersReady,
  })

  const counts = countsData?.org?.rfqCountsByStep

  const showInbound = organization.requestApproval || (counts?.inbound || 0) > 0

  const theSteps = allSteps.map((step) => {
    const countByStepId: Record<string, number | undefined> = {
      all: counts?.all,
      inbound: counts?.inbound,
      submitted: counts?.submitted,
      quoted: counts?.quoted,
      po_sent: counts?.poSent,
      fulfilling: counts?.fulfilling,
    }

    return {
      ...step,
      count: countByStepId[step.id],
      hidden: step.id === 'inbound' && !showInbound,
    }
  })

  const steps = theSteps.filter((step) => !step.hidden)
  const selectedStep = query.selected === 'inbound' && !showInbound ? 'all' : query.selected
  const step = theSteps.filter((s) => s.id === (selectedStep || 'all'))[0]

  const sortBy = (() => {
    if (query.sortBy.field === 'vendors') {
      const field = step.id === 'quoted' ? 'first_store_order_store' : 'first_vendor'
      return { ...query.sortBy, field }
    }

    return query.sortBy
  })()

  const queryResult = useRequestsQuery({
    variables: { orgId, userId, page, filter: apiFilters(step.id), sortBy },
    client,
    fetchPolicy: 'no-cache',
    skip: !filtersReady,
  })

  const { data: orgData } = useRequestsFiltersQuery({
    variables: { orgId },
    client,
    fetchPolicy: 'no-cache',
  })

  // Apply the user's default filters (subscribed locations and location groups) once, then
  // let the heavy queries run. Before, the defaults came from the Requests query itself, so
  // Requests and RequestCounts ran once without them and again with them (gf-g4nif).
  useEffect(() => {
    if (filtersReady) return
    if (defaultsError) {
      setFiltersReady(true)
      return
    }
    const defaultsUser = defaultsData?.user
    if (!defaultsUser) return

    const freshIds = defaultsUser.shippingLocationGroupNotifications.map(
      (n) => n.shippingLocationGroupId
    )

    // Cache for future page loads
    try {
      sessionStorage.setItem(getLocationGroupsCacheKey(userId, orgId), JSON.stringify(freshIds))
    } catch {
      /* ignore */
    }

    const updates = filterDefaultUpdates(
      { locationIds: query.locationIds, locationGroupIds: query.locationGroupIds },
      { locationIds: defaultsUser.locations.map((l) => l.id), locationGroupIds: freshIds },
      locationGroupIdsFromUrl.current
    )

    if (Object.keys(updates).length > 0) updateQuery(updates)
    setFiltersReady(true)
  }, [defaultsData, defaultsError, filtersReady])

  const requestIds = queryResult.data?.searchRequests.requests.map((r) => r.id) ?? []

  const storeOrdersQueryResult = useRequestsStoreOrdersQuery({
    variables: { ids: requestIds },
    client,
    fetchPolicy: 'no-cache',
    skip: requestIds.length === 0,
  })

  const requestStoreOrders: Record<
    string,
    RequestsStoreOrdersQuery['requestForQuotes'][number]['storeOrders']
  > = useMemo(() => {
    if (requestIds.length === 0 || !storeOrdersQueryResult.data) {
      return requestIds.reduce((acc, id) => ({ ...acc, [id]: [] }), {})
    }

    return storeOrdersQueryResult.data.requestForQuotes.reduce(
      (acc, rfq) => ({ ...acc, [rfq.id]: rfq.storeOrders }),
      {}
    )
  }, [requestIds, storeOrdersQueryResult])

  // Listen for user assignment events and refetch data. Before the defaults apply, the
  // queries are skipped; refetch() would run them without the defaults.
  useRfqUserAssignedSubscription({
    variables: { orgId },
    client: useApolloWsClient(),
    onData: () => {
      if (!filtersReady) return
      queryResult.refetch()
      refetchCounts()
    },
  })

  // Listen for RFQ creation events and refetch data
  useRfqCreatedSubscription({
    variables: { orgId },
    client: useApolloWsClient(),
    onData: () => {
      if (!filtersReady) return
      queryResult.refetch()
      refetchCounts()
    },
  })

  const { searchRequests, user } = queryResult.data || {}

  const handleShowActionDivider = (
    scrollLeft: number,
    scrollWidth: number,
    clientWidth: number
  ) => {
    const threshold = 5
    const isScrollAtEnd = scrollLeft + threshold >= scrollWidth - clientWidth
    if (typeof showActionDivider === 'undefined') setShowActionDivider(!isScrollAtEnd)
    else if (isScrollAtEnd && showActionDivider) setShowActionDivider(false)
    else if (!isScrollAtEnd && !showActionDivider) setShowActionDivider(true)
  }

  // Update the action divider when the window resizes or the action divider is undefined
  useEffect(() => {
    if (tableRef.current && searchRequests) {
      handleShowActionDivider(
        tableRef.current.scrollLeft,
        tableRef.current.scrollWidth,
        tableRef.current.clientWidth
      )
    }
  }, [windowWidth, typeof handleShowActionDivider === 'undefined', tableRef, searchRequests])

  const setStep = (index: number) => {
    setPage(undefined)
    setShowActionDivider(undefined)
    const selected = index === 0 ? undefined : steps[index].id
    updateQuery({ selected })
  }

  const setSortBy = (newSortBy: SortByInput) => updateQuery({ sortBy: newSortBy })

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page
        title="Requests"
        actionsNext={[
          <Link.P
            to={`/rfqs/create?source.path=${encodeURIComponent(
              document.location.pathname
            )}&source.button=create-request`}
            key="create-request"
          >
            Order Parts
          </Link.P>,
        ]}
        className="flex flex-col overflow-auto gap-4"
      >
        <FilterProgressNav
          steps={steps}
          selectedIndex={steps.indexOf(step)}
          setSelectedIndex={setStep}
          isFirstStepAnIsland
          textColorClassName="text-orange-500"
          bgColorClassName="bg-orange-500"
        />

        <DelayedSearchInput
          value={query.search}
          setValue={(search) => updateQuery({ search: search.length > 0 ? search : undefined })}
          placeholder="Search requests&hellip;"
        />

        <div className="flex flex-wrap gap-2 items-center">
          <div className="text-sm text-gray-500">Filter by:</div>

          {/* Show the Location Groups filter if the Org has Location Groups or if the filter's been applied */}
          {(orgData?.org?.hasShippingLocationGroups || query.locationGroupIds.length > 0) && (
            <LocationGroupCheckboxCombobox
              locationGroupIds={query.locationGroupIds}
              onChange={(locationGroupIds) => updateQuery({ locationGroupIds: locationGroupIds })}
            />
          )}

          <LocationCheckboxCombobox
            locationIds={query.locationIds}
            onChange={(locationIds) => updateQuery({ locationIds: locationIds })}
          />

          <FilterAction
            selected={query.highPriority}
            onSelect={() => updateQuery({ highPriority: query.highPriority ? undefined : true })}
          >
            High Priority
          </FilterAction>

          <FilterAction
            selected={query.backordered}
            onSelect={() => updateQuery({ backordered: query.backordered ? undefined : true })}
          >
            Backordered
          </FilterAction>

          <FilterAction
            selected={query.myRequests}
            onSelect={() => updateQuery({ myRequests: query.myRequests ? undefined : true })}
          >
            My Requests
          </FilterAction>

          <FilterAction
            selected={query.unassigned}
            onSelect={() => updateQuery({ unassigned: query.unassigned ? undefined : true })}
          >
            Unassigned
          </FilterAction>

          <FilterAction
            selected={query['has-problems']}
            onSelect={() =>
              updateQuery({ 'has-problems': query['has-problems'] ? undefined : true })
            }
          >
            Has Problems
          </FilterAction>

          <FilterDropdown
            title="Delivery method"
            value={query['delivery-method']}
            onChange={(value) => updateQuery({ 'delivery-method': value || undefined })}
            steps={[
              { label: 'All', value: null },
              { label: 'Will Call', value: '2' },
              { label: 'Delivery', value: '1' },
            ]}
          />
        </div>

        <div className="shadow-sm border rounded-md flex flex-col overflow-visible xl:overflow-auto">
          <div
            className="overflow-auto"
            ref={tableRef}
            onScroll={(e) =>
              handleShowActionDivider(
                e.currentTarget.scrollLeft,
                e.currentTarget.scrollWidth,
                e.currentTarget.clientWidth
              )
            }
          >
            <Table className="w-full">
              <Thead>
                <Tr>
                  {step.id === 'closed' && <Th>Fulfillment</Th>}
                  <Th>
                    <SortByHeaderButton
                      field="inserted_at"
                      display="Created"
                      sortBy={query.sortBy}
                      setSortBy={setSortBy}
                    />
                  </Th>
                  <Th>Request</Th>
                  <Th>Machine</Th>
                  {['all', 'inbound'].includes(step.id) && <Th>Assigned To</Th>}

                  {step.id !== 'inbound' && (
                    <Th>
                      <SortByHeaderButton
                        field="vendors"
                        display="Vendors"
                        sortBy={query.sortBy}
                        setSortBy={setSortBy}
                      />
                    </Th>
                  )}

                  {step.id === 'all' && <Th>Status</Th>}
                  <Th>Parts</Th>
                  <Th className="hidden sm:table-cell w-0 sticky right-0 bg-gray-50 bg-opacity-80" />
                  <Th className="table-cell sm:hidden" />
                </Tr>
              </Thead>
              <Tbody data-testid="requests-list">
                {user &&
                  searchRequests?.requests.map((rfq, index) => (
                    <Tr key={rfq.id} data-testid="request-row">
                      {step.id === 'closed' && (
                        <Td>
                          <TimeOpenPill
                            className={rfq.machineDown || rfq.neededBy ? 'px-0' : undefined}
                            createdAt={rfq.insertedAt}
                            closedAt={rfq.timeline.fulfilledAt || rfq.canceledAt || rfq.closedAt}
                          />
                        </Td>
                      )}

                      <Td>
                        <div className="space-y-1">
                          <div>
                            {rfq.insertedAt.year < now.year
                              ? rfq.insertedAt.toLocal().toFormat('D - h:mm a')
                              : rfq.insertedAt.toLocal().toFormat('M/d - h:mm a')}
                          </div>

                          {rfq.creator && (
                            <div className="text-xs text-slate-400 italic">
                              <div>{rfq.creator.displayName}</div>
                            </div>
                          )}
                        </div>
                      </Td>

                      <Td>
                        <Link.T to={`/rfqs/${rfq.id}`} className="text-base">
                          {RequestForQuoteM.shortenId(rfq.id)}
                        </Link.T>
                        {!['all', 'inbound'].includes(step.id) && rfq.assignedUser && (
                          <div className="text-xs text-slate-400 italic pt-1">
                            {rfq.assignedUser.displayName}
                          </div>
                        )}
                      </Td>

                      <Td className="p-0">
                        <div className="text-left block divide-x-1 divide-dotted">
                          {rfq.orgMachines.map((om) => (
                            <Dropdown
                              className="text-left"
                              title={om.name}
                              titleElement={
                                <>
                                  <div className="flex flex-row items-center gap-x-1">
                                    {rfq.machineDown && <MachineDownIcon />}
                                    <p>{om.name}</p>
                                  </div>
                                  <p className="text-xs text-slate-400 italic mt-1 text-left">
                                    {[om.machine.make, om.machine.model].filter(Boolean).join(' ')}
                                  </p>
                                </>
                              }
                              chevronClassName={rfq.machineDown ? 'mt-1' : undefined}
                              stopEventPropagation
                              key={om.id}
                            >
                              <div className="grid grid-cols-3 auto-cols-auto w-80 whitespace-normal text-sm">
                                <span>Serial #</span>
                                <span className="col-span-2">{om.serialNumber}</span>
                                <span>Make</span>
                                <span className="col-span-2">{om.machine.make ?? '—'}</span>
                                <span>Model</span>
                                <span className="col-span-2">{om.machine.model ?? '—'}</span>
                                <span>Year</span>
                                <span className="col-span-2">{om.machine.year ?? '—'}</span>
                              </div>
                            </Dropdown>
                          ))}
                        </div>
                      </Td>

                      {['all', 'inbound'].includes(step.id) && (
                        <Td className="p-0">
                          <AssignUserDropdown
                            rfq={rfq}
                            onUserAssigned={queryResult.refetch}
                            menuClassName="mt-2 ml-4"
                            trigger={
                              <span className="truncate max-w-72 hover:bg-blue-100 px-6 py-4">
                                {rfq.assignedUser?.displayName ?? (
                                  <span className="italic text-gray-500">Unassigned</span>
                                )}{' '}
                                <ChevronDownIcon className="w-5 h-5 inline-block" />
                              </span>
                            }
                          />
                          {orgData?.org?.hasShippingLocationGroups && rfq.shippingLocationGroup && (
                            <div className="px-6 pb-3 mt-1 text-xs text-slate-400 italic">
                              {rfq.shippingLocationGroup.name}
                            </div>
                          )}
                        </Td>
                      )}

                      {step.id !== 'inbound' ? (
                        step.id === 'quoted' ? (
                          <Td className="p-0">
                            {' '}
                            <Stores stores={requestStoreOrders[rfq.id].map((so) => so.store)} />
                          </Td>
                        ) : (
                          <Td className="p-0">
                            <Vendors rfq={rfq} />
                          </Td>
                        )
                      ) : null}

                      {step.id === 'all' && (
                        <Td>
                          <RfqStepBadge step={rfq.step} />
                        </Td>
                      )}

                      <PartsCell rfq={rfq} storeOrders={requestStoreOrders[rfq.id]} />

                      <ActionCells
                        showActionDivider={showActionDivider}
                        firstOrLast={index === 0 || index === searchRequests.requests.length - 1}
                      >
                        <Actions
                          user={user}
                          rfq={rfq}
                          rfqStoreOrders={requestStoreOrders[rfq.id]}
                          onCanceled={() => {
                            queryResult.refetch()
                            refetchCounts()
                          }}
                        />
                      </ActionCells>
                    </Tr>
                  ))}

                {!searchRequests && (
                  <Tr>
                    <Td colSpan={100}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}

                {searchRequests?.requests.length === 0 && (
                  <Tr>
                    <Td colSpan={100}>
                      No{selectedStep !== 'all' && <> {selectedStep}</>} requests found.
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </div>
        </div>

        <PaginationC pagination={searchRequests?.pagination} page={page} updatePage={setPage} />
      </Page>
    </Frame>
  )
}

export default Requests
